import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users, Archive, BookOpen, Heart, Activity, Database,
  Mail, HardDrive, Search, Shield, ChevronDown, ChevronUp,
  BarChart3, TrendingUp, MessageSquare, Facebook, Lock, Eye, EyeOff,
  Gift, Trophy, Crown, Plus, Save
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell
} from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";

const GOLD = "#00FF88";
const PIE_COLORS = ["#00FF88", "#22C55E", "#3B82F6", "#F97316", "#8B5CF6"];

function StatCard({ icon: Icon, label, value, sub, color = "white" }) {
  return (
    <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 hover:border-white/10 transition-colors">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          color === "gold" ? "bg-[#00FF88]/10" : "bg-white/5"
        }`}>
          <Icon className={`w-5 h-5 ${color === "gold" ? "text-[#00FF88]" : "text-slate-400"}`} strokeWidth={1.5} />
        </div>
        <span className="text-xs text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-3xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#111] border border-white/10 rounded-xl px-4 py-2.5 shadow-xl">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
}

export default function SuperAdminPage() {
  const [activeTab, setActiveTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [system, setSystem] = useState(null);
  const [users, setUsers] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [prizeConfig, setPrizeConfig] = useState({
    prizes: [
      { place: 1, title: "Grand Prize", description: "Top weekly contributor", value: "" },
      { place: 2, title: "Premium Reward", description: "Second place", value: "" },
      { place: 3, title: "Special Gift", description: "Third place", value: "" },
    ],
    announcement: ""
  });
  const [savingPrizes, setSavingPrizes] = useState(false);
  const [pastWinners, setPastWinners] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === "users") loadUsers();
    if (activeTab === "prizes") loadPrizes();
  }, [activeTab, userPage, userSearch]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [analyticsRes, systemRes] = await Promise.all([
        api.get("/superadmin/analytics"),
        api.get("/superadmin/system"),
      ]);
      setAnalytics(analyticsRes.data);
      setSystem(systemRes.data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to load admin data";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await api.get("/superadmin/users", {
        params: { search: userSearch, skip: userPage * 25, limit: 25 }
      });
      setUsers(res.data.users);
      setUserTotal(res.data.total);
    } catch { /* ignore */ }
  };

  const loadPrizes = async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        api.get("/prizes/current"),
        api.get("/prizes/history?limit=10")
      ]);
      if (!currentRes.data.is_default) {
        setPrizeConfig({
          prizes: currentRes.data.prizes || prizeConfig.prizes,
          announcement: currentRes.data.announcement || ""
        });
      }
      setPastWinners(historyRes.data.winners || []);
    } catch { /* ignore */ }
  };

  const savePrizeConfig = async () => {
    setSavingPrizes(true);
    try {
      await api.post("/prizes/configure", prizeConfig);
      toast.success("Prizes saved successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save prizes");
    } finally {
      setSavingPrizes(false);
    }
  };

  const announceWinners = async (weekKey) => {
    if (!window.confirm(`Announce winners for ${weekKey}?`)) return;
    try {
      await api.post("/prizes/announce-winners", { week_key: weekKey });
      toast.success("Winners announced!");
      loadPrizes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to announce winners");
    }
  };

  const updatePrize = (place, field, value) => {
    setPrizeConfig(prev => ({
      ...prev,
      prizes: prev.prizes.map(p => p.place === place ? { ...p, [field]: value } : p)
    }));
  };

  const toggleUserDisabled = async (userId, currentlyDisabled) => {
    try {
      await api.put(`/superadmin/users/${userId}`, { is_disabled: !currentlyDisabled });
      toast.success(currentlyDisabled ? "User enabled" : "User disabled");
      loadUsers();
    } catch {
      toast.error("Failed to update user");
    }
  };

  const toggleSuperadmin = async (userId, currentlySuper) => {
    if (!window.confirm(currentlySuper ? "Revoke superadmin?" : "Grant superadmin access?")) return;
    try {
      await api.put(`/superadmin/users/${userId}`, { is_superadmin: !currentlySuper });
      toast.success(currentlySuper ? "Superadmin revoked" : "Superadmin granted");
      loadUsers();
    } catch {
      toast.error("Failed to update");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <div className="w-8 h-8 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-12 gap-4">
        <Shield className="w-12 h-12 text-red-400" />
        <p className="text-lg font-medium text-white" style={{ fontFamily: 'Manrope' }}>Access Denied</p>
        <p className="text-sm text-slate-400">{error}</p>
        <Button onClick={loadData} className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-6 mt-4">
          Retry
        </Button>
      </div>
    );
  }

  const tabs = [
    { key: "analytics", label: "Analytics", icon: BarChart3 },
    { key: "content", label: "Content", icon: Archive },
    { key: "users", label: "Users", icon: Users },
    { key: "prizes", label: "Prizes", icon: Gift },
    { key: "system", label: "System", icon: Activity },
  ];

  const t = analytics?.totals || {};
  const vaultBreakdown = Object.entries(analytics?.vault_breakdown || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="p-6 md:p-10 max-w-[1400px] mx-auto" data-testid="superadmin-page">
      <div className="mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Superadmin</p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Admin Dashboard
        </h1>
        <p className="text-sm text-slate-400 mt-1">Monitor and manage your Black Box platform</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-8 bg-white/[0.02] border border-white/[0.06] rounded-xl p-1.5 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`admin-tab-${tab.key}`}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              activeTab === tab.key
                ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"
                : "text-slate-500 hover:text-white hover:bg-white/5"
            }`}
          >
            <tab.icon className="w-4 h-4" strokeWidth={1.5} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── Analytics Tab ─── */}
      {activeTab === "analytics" && analytics && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
          {/* Top stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Total Users" value={t.users} color="gold" />
            <StatCard icon={TrendingUp} label="Active (7d)" value={analytics.active_users_7d} sub="Users with recent activity" color="gold" />
            <StatCard icon={Archive} label="Vault Items" value={t.vault_entries} />
            <StatCard icon={BookOpen} label="Journal Entries" value={t.journal_entries} />
          </div>

          {/* Signup Chart */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
            <h3 className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'Manrope' }}>User Signups</h3>
            <p className="text-xs text-slate-500 mb-6">Last 30 days</p>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={analytics.signup_chart}>
                <defs>
                  <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GOLD} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: '#64748B' }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="signups" stroke={GOLD} fill="url(#goldGrad)" strokeWidth={2} name="Signups" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Content Activity Chart */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
            <h3 className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'Manrope' }}>Content Activity</h3>
            <p className="text-xs text-slate-500 mb-6">Vault + Journal entries created daily</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={analytics.content_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: '#64748B' }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="content" fill={GOLD} radius={[4, 4, 0, 0]} name="Content Created" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* ─── Content Tab ─── */}
      {activeTab === "content" && analytics && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <StatCard icon={Archive} label="Vault Items" value={t.vault_entries} color="gold" />
            <StatCard icon={BookOpen} label="Journal Entries" value={t.journal_entries} />
            <StatCard icon={Heart} label="Legacy Messages" value={t.legacy_messages} />
            <StatCard icon={Activity} label="Prompt Responses" value={t.prompt_responses} />
            <StatCard icon={Users} label="Beneficiaries" value={t.beneficiaries} />
            <StatCard icon={MessageSquare} label="Chat Messages" value={t.chat_messages} />
          </div>

          {/* Vault Breakdown */}
          {vaultBreakdown.length > 0 && (
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
              <h3 className="text-base font-semibold text-white mb-6" style={{ fontFamily: 'Manrope' }}>Vault Content Breakdown</h3>
              <div className="flex flex-col md:flex-row items-center gap-8">
                <ResponsiveContainer width={200} height={200}>
                  <PieChart>
                    <Pie data={vaultBreakdown} cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={3} dataKey="value">
                      {vaultBreakdown.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-4">
                  {vaultBreakdown.map((item, i) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-sm text-slate-300 capitalize">{item.name.replace("_", " ")}</span>
                      <span className="text-sm font-bold text-white">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* ─── Users Tab ─── */}
      {activeTab === "users" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search by name, email, or ID..."
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); setUserPage(0); }}
                data-testid="admin-user-search"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-xl h-12 pl-11"
              />
            </div>
            <p className="text-sm text-slate-500">{userTotal} users total</p>
          </div>

          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/[0.06] text-xs text-slate-500 uppercase tracking-wider">
              <div className="col-span-3">User</div>
              <div className="col-span-2">Auth</div>
              <div className="col-span-2">Content</div>
              <div className="col-span-2">Joined</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {/* Rows */}
            {users.map((u) => (
              <div key={u.user_id}>
                <div
                  className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors cursor-pointer items-center"
                  onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                  data-testid={`admin-user-row-${u.user_id}`}
                >
                  <div className="col-span-3 flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-xs font-bold text-slate-400 shrink-0">
                      {(u.name || u.email || "?")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{u.name || "—"}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400">
                      {u.auth_type || "email"}
                    </Badge>
                    {u.is_superadmin && (
                      <Badge className="text-[10px] bg-[#00FF88]/10 text-[#00FF88] border-[#00FF88]/20 ml-1">admin</Badge>
                    )}
                  </div>
                  <div className="col-span-2 flex gap-3 text-xs text-slate-400">
                    <span>{u.stats?.vault || 0} vault</span>
                    <span>{u.stats?.journal || 0} journal</span>
                  </div>
                  <div className="col-span-2 text-xs text-slate-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </div>
                  <div className="col-span-1">
                    {u.is_disabled ? (
                      <Badge variant="outline" className="text-[10px] border-red-500/20 text-red-400">disabled</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-emerald-500/20 text-emerald-400">active</Badge>
                    )}
                  </div>
                  <div className="col-span-2 flex justify-end gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleUserDisabled(u.user_id, u.is_disabled); }}
                      data-testid={`toggle-disable-${u.user_id}`}
                      className="p-2 rounded-lg hover:bg-white/5 text-slate-500 hover:text-white transition-colors"
                      title={u.is_disabled ? "Enable user" : "Disable user"}
                    >
                      {u.is_disabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSuperadmin(u.user_id, u.is_superadmin); }}
                      data-testid={`toggle-admin-${u.user_id}`}
                      className={`p-2 rounded-lg hover:bg-white/5 transition-colors ${
                        u.is_superadmin ? "text-[#00FF88]" : "text-slate-500 hover:text-white"
                      }`}
                      title={u.is_superadmin ? "Revoke superadmin" : "Grant superadmin"}
                    >
                      <Shield className="w-4 h-4" />
                    </button>
                    {expandedUser === u.user_id ? (
                      <ChevronUp className="w-4 h-4 text-slate-500 mt-2" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-500 mt-2" />
                    )}
                  </div>
                </div>
                {/* Expanded details */}
                {expandedUser === u.user_id && (
                  <div className="px-6 py-4 bg-white/[0.01] border-b border-white/[0.03] grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div><span className="text-slate-600">User ID:</span> <span className="text-slate-300 font-mono">{u.user_id}</span></div>
                    <div><span className="text-slate-600">Onboarding:</span> <span className="text-slate-300">{u.onboarding_complete ? "Complete" : `Step ${u.onboarding_step || 0}`}</span></div>
                    <div><span className="text-slate-600">Legacy msgs:</span> <span className="text-slate-300">{u.stats?.legacy || 0}</span></div>
                    <div><span className="text-slate-600">Account status:</span> <span className="text-slate-300">{u.account_status || "active"}</span></div>
                  </div>
                )}
              </div>
            ))}
            {users.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-slate-500">No users found</div>
            )}
          </div>

          {/* Pagination */}
          {userTotal > 25 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Showing {userPage * 25 + 1}-{Math.min((userPage + 1) * 25, userTotal)} of {userTotal}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={userPage === 0}
                  onClick={() => setUserPage(userPage - 1)}
                  className="border-white/10 text-slate-400 rounded-lg"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(userPage + 1) * 25 >= userTotal}
                  onClick={() => setUserPage(userPage + 1)}
                  className="border-white/10 text-slate-400 rounded-lg"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* ─── Prizes Tab ─── */}
      {activeTab === "prizes" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
          {/* Configure This Week's Prizes */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Gift className="w-5 h-5 text-[#00FF88]" />
                <h3 className="text-base font-semibold text-white" style={{ fontFamily: 'Manrope' }}>
                  Configure Weekly Prizes
                </h3>
              </div>
              <Button
                onClick={savePrizeConfig}
                disabled={savingPrizes}
                className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-5"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingPrizes ? "Saving..." : "Save Prizes"}
              </Button>
            </div>

            {/* Announcement */}
            <div className="mb-6">
              <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
                Announcement Message (optional)
              </label>
              <Input
                placeholder="e.g., This week's prizes sponsored by..."
                value={prizeConfig.announcement}
                onChange={(e) => setPrizeConfig({ ...prizeConfig, announcement: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
              />
            </div>

            {/* Prize Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {prizeConfig.prizes.map((prize) => (
                <div
                  key={prize.place}
                  className={`rounded-xl p-5 border ${
                    prize.place === 1
                      ? "bg-[#FFD700]/5 border-[#FFD700]/30"
                      : prize.place === 2
                      ? "bg-[#C0C0C0]/5 border-[#C0C0C0]/30"
                      : "bg-[#CD7F32]/5 border-[#CD7F32]/30"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">
                      {prize.place === 1 ? "🥇" : prize.place === 2 ? "🥈" : "🥉"}
                    </span>
                    <span className="text-sm font-bold text-white">Place {prize.place}</span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase">Title</label>
                      <Input
                        value={prize.title}
                        onChange={(e) => updatePrize(prize.place, "title", e.target.value)}
                        className="bg-white/5 border-white/10 text-white text-sm rounded-lg h-9"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase">Description</label>
                      <Input
                        value={prize.description}
                        onChange={(e) => updatePrize(prize.place, "description", e.target.value)}
                        className="bg-white/5 border-white/10 text-white text-sm rounded-lg h-9"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase">Prize Value</label>
                      <Input
                        placeholder="e.g., $500 Gift Card"
                        value={prize.value}
                        onChange={(e) => updatePrize(prize.place, "value", e.target.value)}
                        className="bg-white/5 border-white/10 text-white text-sm rounded-lg h-9"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Announce Winners */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Trophy className="w-5 h-5 text-[#00FF88]" />
              <h3 className="text-base font-semibold text-white" style={{ fontFamily: 'Manrope' }}>
                Announce Winners
              </h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Select a week to announce winners. This will record the top 3 users and their prizes.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  const now = new Date();
                  const lastWeekStart = new Date(now);
                  lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
                  const weekKey = `${lastWeekStart.getFullYear()}-W${String(Math.ceil((lastWeekStart - new Date(lastWeekStart.getFullYear(), 0, 1)) / 604800000)).padStart(2, '0')}`;
                  announceWinners(weekKey);
                }}
                variant="outline"
                className="border-[#00FF88]/30 text-[#00FF88] hover:bg-[#00FF88]/10 rounded-full"
              >
                <Crown className="w-4 h-4 mr-2" />
                Announce Last Week's Winners
              </Button>
            </div>
          </div>

          {/* Past Winners */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Crown className="w-5 h-5 text-[#00FF88]" />
              <h3 className="text-base font-semibold text-white" style={{ fontFamily: 'Manrope' }}>
                Past Winners
              </h3>
            </div>
            {pastWinners.length === 0 ? (
              <p className="text-sm text-slate-500">No winners announced yet</p>
            ) : (
              <div className="space-y-4">
                {pastWinners.map((week) => (
                  <div key={week.week_key} className="border border-white/[0.06] rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-white">{week.week_key}</span>
                      <span className="text-xs text-slate-500">
                        {new Date(week.announced_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex gap-4">
                      {week.winners?.map((w) => (
                        <div key={w.place} className="flex items-center gap-2">
                          <span>{w.place === 1 ? "🥇" : w.place === 2 ? "🥈" : "🥉"}</span>
                          <span className="text-sm text-slate-300">{w.name}</span>
                          <span className="text-xs text-slate-500">({w.points} pts)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* ─── System Tab ─── */}
      {activeTab === "system" && system && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
          {/* Config Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`bg-white/[0.03] backdrop-blur-xl border rounded-2xl p-5 ${system.config?.fb_configured ? "border-emerald-500/20" : "border-amber-500/20"}`}>
              <div className="flex items-center gap-3 mb-2">
                <Facebook className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium text-white">Facebook API</span>
              </div>
              <Badge variant="outline" className={`text-xs ${system.config?.fb_configured ? "border-emerald-500/30 text-emerald-400" : "border-amber-500/30 text-amber-400"}`}>
                {system.config?.fb_configured ? "Configured" : "Not Configured (Demo Mode)"}
              </Badge>
            </div>
            <div className={`bg-white/[0.03] backdrop-blur-xl border rounded-2xl p-5 ${system.config?.smtp_configured ? "border-emerald-500/20" : "border-amber-500/20"}`}>
              <div className="flex items-center gap-3 mb-2">
                <Mail className="w-5 h-5 text-slate-400" />
                <span className="text-sm font-medium text-white">Email (SMTP)</span>
              </div>
              <Badge variant="outline" className={`text-xs ${system.config?.smtp_configured ? "border-emerald-500/30 text-emerald-400" : "border-amber-500/30 text-amber-400"}`}>
                {system.config?.smtp_configured ? "Configured" : "Not Configured (Queued Only)"}
              </Badge>
            </div>
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-5 h-5 text-[#00FF88]" />
                <span className="text-sm font-medium text-white">Superadmins</span>
              </div>
              <Badge variant="outline" className="text-xs border-white/10 text-slate-300">
                {system.config?.superadmin_emails || 0} configured
              </Badge>
            </div>
          </div>

          {/* Storage & Sessions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={HardDrive} label="Storage Used" value={`${system.storage?.total_mb || 0} MB`} sub={`${system.storage?.files_count || 0} files`} color="gold" />
            <StatCard icon={Database} label="Active Sessions" value={system.sessions?.active || 0} />
            <StatCard icon={MessageSquare} label="Total Chats" value={system.chat?.total_messages || 0} />
            <StatCard icon={Lock} label="Claimed Accounts" value={system.accounts?.claimed || 0} />
          </div>

          {/* Facebook Stats */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
            <h3 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Facebook Connections</h3>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-2xl font-bold text-white">{system.facebook?.total_connections || 0}</p>
                <p className="text-xs text-slate-500">Total Connections</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-400">{system.facebook?.real_connections || 0}</p>
                <p className="text-xs text-slate-500">Real (OAuth)</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{system.facebook?.demo_connections || 0}</p>
                <p className="text-xs text-slate-500">Demo Mode</p>
              </div>
            </div>
          </div>

          {/* Email Queue */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
            <h3 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Email Queue</h3>
            <div className="grid grid-cols-4 gap-6 mb-6">
              <div>
                <p className="text-2xl font-bold text-white">{system.email_queue?.total || 0}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{system.email_queue?.queued || 0}</p>
                <p className="text-xs text-slate-500">Queued</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-400">{system.email_queue?.sent || 0}</p>
                <p className="text-xs text-slate-500">Sent</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-400">{system.email_queue?.failed || 0}</p>
                <p className="text-xs text-slate-500">Failed</p>
              </div>
            </div>
            {system.email_queue?.recent?.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Recent Emails</p>
                {system.email_queue.recent.map((email, i) => (
                  <div key={email.email_id || i} className="flex items-center justify-between px-4 py-2.5 bg-white/[0.02] rounded-xl">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{email.subject}</p>
                      <p className="text-xs text-slate-500">{email.to_email}</p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ml-3 shrink-0 ${
                      email.status === "sent" ? "border-emerald-500/20 text-emerald-400" :
                      email.status === "failed" ? "border-red-500/20 text-red-400" :
                      "border-amber-500/20 text-amber-400"
                    }`}>{email.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}
